import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError, AxiosResponse } from 'axios';
import { DefaultTFuncReturn } from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { UseFormRegister, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  usePostIllustListDownloadIllust,
  usePostIllustListDownloadIllusts,
  usePostIllustListDownloadMultipleLanguageIllusts,
  usePostIllustListRemoveUnusedIllusts,
} from '../../../../api/illust-list/illust-list';
import { IllustList, IllustListItem, ProductionNo } from '../../../../api/model';
import { usePostUtilsDownload } from '../../../../api/utils/utils';
import { useDefaultOptions, useOptionsForBinary } from '../../../../hooks/api';
import yup from '../../../../utils/custom-yup';
import { executeDownload } from '../../../../utils/download';
import * as sessionStorageUtils from '../../../../utils/sessionStorage';
import { CheckBox } from '../../../atoms/CheckBox';
import SingleSelect from '../../../atoms/SingleSelect';
// import MultiSelect from '../../../atoms/MultiSelect';
// import NoDataComponent from '../../../atoms/NoDataComponent';
import BlobErrorModal from '../../../modules/BlobErrorModal';
import ConfirmModal from '../../../modules/ConfirmModal';
import ErrorModal from '../../../modules/ErrorModal';
import Modal from '../../../modules/Modal';
import OkModal from '../../../modules/OkModal';
import PageNavi from '../../../modules/PageNavi';
import SpinnerModal from '../../../modules/SpinnerModal';

// 1ページに表示する件数
const N_DATA_PER_PAGE = 50;

type SortKey = 'illust_list_item_id' | 'file_name' | 'production_no' | 'chapter_name' | 'updated_at';

interface SortableThProps {
  label: DefaultTFuncReturn;
  onClick: () => void;
}

/**
 * ソート可能なテーブルヘッダー
 * @param param0
 * @returns
 */
function SortableTh({ label, onClick }: SortableThProps) {
  return (
    <th>
      <button type="button" className="text-button" onClick={onClick}>
        {label}
        <i className="fa-solid fa-sort" />
      </button>
    </th>
  );
}

interface IllustTrProps {
  data: IllustListItem;
  visible: boolean;
  register: UseFormRegister<{
    [key: string]: boolean;
  }>;
}

/**
 * イラストリストの各行
 * @param param0
 * @returns
 */
function IllustTr({ data, visible, register }: IllustTrProps) {
  // 言語情報
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // ダウンロード API
  const options = useOptionsForBinary(language);
  const {
    mutateAsync: downloadAsync,
    error: downloadError,
    isError: downloadIsError,
  } = usePostIllustListDownloadIllust<AxiosError<Blob>>(options);

  // ダウンロードエラーの表示
  const [displayDownloadError, setDisplayDownloadError] = useState(false);
  useEffect(() => {
    setDisplayDownloadError(downloadIsError);
  }, [downloadIsError]);

  // 複数一括ダウンロード API
  const {
    mutateAsync: multipleDownloadAsync,
    error: multipleDownloadError,
    isError: multipleDownloadIsError,
  } = usePostIllustListDownloadMultipleLanguageIllusts<AxiosError<Blob>>(options);

  // 複数一括ダウンロードエラーの表示
  const [displayMultipleDownloadError, setDisplayMultipleDownloadError] = useState(false);
  useEffect(() => {
    setDisplayMultipleDownloadError(multipleDownloadIsError);
  }, [multipleDownloadIsError]);

  // 検索条件部分の開閉
  const [open, setOpen] = useState(false);

  // API 実行中
  const [isSubmitting, setIsSubmitting] = useState(false);

  /*
  if (!visible) {
    return <input type="hidden" name={data.illust_list_item_id?.toString() ?? ''} />;
  }
  */

  return (
    <tr key={data.illust_list_item_id}>
      <td>
        <CheckBox
          label={undefined}
          name={data.illust_list_item_id}
          isDisabled={false}
          value={undefined}
          register={register}
        />
      </td>
      <td>
        <img src={data.url} alt="" style={{ maxWidth: '250px' }} />
      </td>
      <td>{data.file_name}</td>
      <td>
        {data.production_id != null && data.section_id != null ? (
          <Link to={`/${language}/productions/sections/${data.production_id}/ps?section_id=${data.section_id}`} target="_blank">
            {data.production_no}
          </Link>
        ) : (
          data.production_no
        )}
      </td>
      <td>{data.chapter_name}</td>
      <td>{data.updated_at?.replaceAll('-', '/')}</td>
      <td className="elloutput">
        <button
          type="button"
          className="text-button"
          onClick={() => {
            setOpen((pre) => !pre);
          }}
        >
          <i className="fa-solid fa-ellipsis-vertical" />
        </button>
        <div className={open ? 'open' : undefined}>
          <ul>
            <li>
              <button
                type="button"
                className="text-button"
                onClick={async () => {
                  setIsSubmitting(true);
                  await downloadAsync(
                    { data: { illust_list_item_id: data.illust_list_item_id } },
                    {
                      onSuccess: (response) => {
                        executeDownload(response);
                        setIsSubmitting(false);
                      },
                      onError: () => setIsSubmitting(false),
                    }
                  );
                }}
              >
                <i className="fa-solid fa-upload" />
                {t('ダウンロード')}
              </button>
            </li>

            {/* 他言語一括ダウンロード */}
            {/* 
            {data.illust_list_item_id.substring(0, 3) === 'PD_' && (
              <li>
                <button
                  type="button"
                  className="text-button"
                  onClick={async () => {
                    setIsSubmitting(true);
                    await multipleDownloadAsync(
                      { data: { illust_list_item_id: data.illust_list_item_id } },
                      {
                        onSuccess: (response) => {
                          executeDownload(response);
                          setIsSubmitting(false);
                        },
                        onError: () => setIsSubmitting(false),
                      }
                    );
                  }}
                >
                  <i className="fa-solid fa-upload" />
                  {t('他言語一括ダウンロード')}
                </button>
              </li>
            )}
            */}
          </ul>
        </div>
      </td>
      {isSubmitting && <SpinnerModal />}
      {displayDownloadError && (
        <BlobErrorModal error={downloadError} onClickOk={() => setDisplayDownloadError(false)} />
      )}
      {displayMultipleDownloadError && (
        <BlobErrorModal error={multipleDownloadError} onClickOk={() => setDisplayMultipleDownloadError(false)} />
      )}
    </tr>
  );
}

interface SelectProductionNoModalProps {
  productionNos: ProductionNo[] | undefined;
  onClose: () => void;
  refetch: () => void;
}

/**
 * 不要イラスト削除（機種選択）モーダル
 * @param param0
 * @returns
 */
function SelectProductionNoModal({ productionNos, onClose, refetch }: SelectProductionNoModalProps) {
  // 言語情報
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const teamUserMessage = t('資料管理No.を選択してください');
  const schema = yup.object().shape({
    production_id: yup.string().required(teamUserMessage),
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{ production_id: number | undefined }>({ resolver: yupResolver(schema) });

  const defaultOptions = useDefaultOptions(language);
  const optionsForBinary = useOptionsForBinary(language);
  const [retMessage, setRetMessage] = useState(0); // 処理メッセージ
  const [displayError, setDisplayError] = useState(false); // エラー表示
  const [displayDownLoadError, setDisplayDownLoadError] = useState(false); // ダウンロードエラー
  const [displayConfirming, setDisplayConfirming] = useState(false); // 確認ダイアログの表示
  const [resultMessge, setResultMessage] = useState<string | undefined>('');

  // 未使用イラスト一括削除 API
  const { mutateAsync, isLoading, error, isError } = usePostIllustListRemoveUnusedIllusts(defaultOptions);
  useEffect(() => {
    setDisplayError(isError);
  }, [isError, onClose]);

  // ダウンロードAPI
  const {
    mutateAsync: _downloadAsync,
    isError: downloadIsError,
    error: downloadError,
  } = usePostUtilsDownload<AxiosError<Blob>>(optionsForBinary);
  useEffect(() => {
    setDisplayDownLoadError(downloadIsError);
  }, [downloadIsError]);

  return (
    <Modal active additionalClassName="popupfr340" displayCloseButton onClose={onClose}>
      <div className="select-production-id">
        <div>
          <label htmlFor="production_id">{t('資料管理No')}</label>
        </div>
        {/* 
        <div style={{ marginTop: '10px' }}>
          <strong>{t('これはテスト版です。現在実行してもイラストファイルは削除されません')}</strong>
        </div>
        */}
        <div className="mt20 mb20">
          <SingleSelect
            id="production_id"
            name="production_id"
            allowEmpty
            options={productionNos ?? []}
            register={register}
            getValue={(x) => x.production_id}
            getLabel={(x) => x.production_no}
          />
        </div>
        {errors.production_id?.message && <p className="errormessage show">{errors.production_id?.message}</p>}
      </div>
      <div className="mb0">
        <input
          type="button"
          className="red-button"
          value={t('はい').toString()}
          onClick={handleSubmit(() => {
            setDisplayConfirming(true);
          })}
        />
        <input type="button" className="red-button ml10" value={t('キャンセル').toString()} onClick={onClose} />
      </div>
      {/* 確認ダイアログ表示 */}
      {displayConfirming && (
        <ConfirmModal
          onClickCancel={() => setDisplayConfirming(false)}
          onClickOk={handleSubmit(async (data) => {
            setDisplayConfirming(false);
            const response = await mutateAsync({ data });
            if (response.status === 200) {
              // 処理が成功したら結果をダウンロード
              if (response.data.result_file_info !== undefined && response.data.result_file_info !== null) {
                setResultMessage(response.data.message);
                await _downloadAsync(
                  { data: response.data.result_file_info },
                  {
                    onSuccess: (responseDL) => {
                      executeDownload(responseDL);
                      setRetMessage(1);
                      void refetch();
                    },
                  }
                );
              } else {
                setRetMessage(2);
              }
            }
          })}
        >
          {t('不要イラストを削除しますか？')}
        </ConfirmModal>
      )}
      {/* 待ちモーダル */}
      {isLoading && <SpinnerModal />}
      {/* エラーモーダル */}
      {displayError && <ErrorModal error={error} onClickOk={() => setDisplayError(false)} />}
      {/* ログダウンロードエラー */}
      {displayDownLoadError && (
        <BlobErrorModal error={downloadError} onClickOk={() => setDisplayDownLoadError(false)} />
      )}
      {retMessage === 1 && (
        <OkModal
          onClickOk={() => {
            setRetMessage(0);
            onClose();
          }}
        >
          <div>{resultMessge}</div>
        </OkModal>
      )}
      {retMessage === 2 && (
        <OkModal onClickOk={() => setRetMessage(0)}>
          <div>
            {t(
              '不要ファイル削除処理がエラーなしで終了しましたが、ログを取得できませんでした。管理者にご連絡ください。'
            )}
          </div>
        </OkModal>
      )}
    </Modal>
  );
}

interface IllustListProps {
  data: AxiosResponse<IllustList> | undefined;
  productionNos: ProductionNo[] | undefined;
  refetch: () => void;
}

/**
 * イラストリスト
 * @param param0
 * @returns
 */
function IllustListComponent({ data, productionNos, refetch }: IllustListProps) {
  // 言語情報
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // 現在表示中のページ番号
  const [currentPage, setCurrentPage] = useState(sessionStorageUtils.get('Illusts.CurrentPage', 1));

  // ソート情報
  const [sortInfo, setSortInfo] = useState(
    // sessionStorageUtils.get('Illusts.SortInfo', { key: 'illust_list_item_id' as SortKey, isDisc: false })
    sessionStorageUtils.get('Illusts.SortInfo', { key: 'production_no' as SortKey, isDisc: false })
  );

  // データ取得のオプション
  const optionsForBinary = useOptionsForBinary(language);

  // フォーム情報
  const { handleSubmit, register, reset } = useForm<{ [key: string]: boolean }>();

  // API 実行中かどうか
  const [isSubmitting, setIsSubmitting] = useState(false);

  // メッセージ表示
  const [displayMessageNotSelect, setDisplayMessageNotSelect] = useState(false);

  // 不要イラスト削除可能
  const [allRvIllust, setAllRvIllust] = useState(false);

  // 複数一括ダウンロード API
  const {
    mutateAsync: downloadMultipleAsync,
    error: downloadMultipleError,
    isError: downloadMultipleIsError,
  } = usePostIllustListDownloadIllusts<AxiosError<Blob>>(optionsForBinary);

  // 複数一括ダウンロード API のエラー表示
  const [displayDownloadMultipleError, setDisplayDownloadMultipleError] = useState(false);

  useEffect(() => {
    setDisplayDownloadMultipleError(downloadMultipleIsError);
  }, [downloadMultipleIsError]);

  // 現在ページを変更する関数
  const changeCurrentPage = useCallback(
    (next: number) => {
      sessionStorageUtils.set('Illusts.CurrentPage', next);
      setCurrentPage(next);
    },
    [setCurrentPage]
  );

  // 一覧のソートキーを変更する関数
  const changeSortKey = useCallback((key: SortKey) => {
    setSortInfo((pre) => {
      const next = { key, isDisc: pre.key === key && !pre.isDisc };
      sessionStorageUtils.set('Illusts.SortInfo', next);
      return next;
    });
    setCurrentPage(1);
  }, []);

  // 一覧のソート結果
  const sortedIllustList = useMemo(
    () =>
      [...(data?.data.illust_list_items ?? [])].sort((a, b) => {
        const aAny = a as unknown as { [key: string]: string | number };
        const bAny = b as unknown as { [key: string]: string | number };
        const multiValue = sortInfo.isDisc ? -1 : 1;
        if (aAny[sortInfo.key] !== bAny[sortInfo.key]) {
          // 第一ソートキー
          return ((aAny[sortInfo.key] ?? '') > (bAny[sortInfo.key] ?? '') ? 1 : -1) * multiValue;
        }
        // 第三ソートキー
        return ((a.illust_list_item_id ?? '') > (b.illust_list_item_id ?? '') ? 1 : -1) * multiValue;
      }),
    [sortInfo, data]
  );

  // 機種選択モーダル
  const [displayProductionNoModal, setDisplayProductionNoModal] = useState(false);

  useEffect(() => {
    if (productionNos !== undefined && productionNos !== null && productionNos.length > 0) {
      setAllRvIllust(true);
    } else {
      setAllRvIllust(false);
    }
  }, [productionNos]);

  /*
  // データ取得中
  if (isLoading) {
    return <WaitComponent />;
  }

  // エラー表示
  if (isError) {
    return <ErrorComponent error={error} />;
  }
  */

  return (
    <div className="wfr documentlist in-pgnavi">
      <div className="master-navi groupeu mt10">
        <ul>
          {/* ダウンロードボタン */}
          <li className="mr10">
            <button
              type="button"
              className="text-button"
              onClick={() => {
                void handleSubmit(async (newData) => {
                  if (data?.data.illust_list_items == null) return;
                  const targetIds = data?.data.illust_list_items
                    .filter((x) => newData[x.illust_list_item_id])
                    .map((x) => x.illust_list_item_id);
                  if (targetIds.length === 0) {
                    setDisplayMessageNotSelect(true);
                    return;
                  }
                  setIsSubmitting(true);
                  await downloadMultipleAsync(
                    { data: { illust_list_item_ids: targetIds } },
                    {
                      onSuccess: (response) => {
                        executeDownload(response);
                        setIsSubmitting(false);
                      },
                    }
                  );
                })();
              }}
            >
              <i className="fa-solid fa-download" />
              {t('ダウンロード')}
            </button>
          </li>
          {/* 不要イラスト削除ボタン */}
          <li className="modal-open" onClick={() => allRvIllust && setDisplayProductionNoModal(true)}>
            <span className={allRvIllust ? 'span-fa-enable' : 'span-fa-disable'}>
              <i className="fa-solid fa-circle-xmark" />
              {t('不要イラスト削除')}
            </span>
          </li>
        </ul>
      </div>
      {/* 一覧部分 */}
      <table className="table46">
        <thead>
          <tr>
            <th>
              <span className="ck-box">
                <input
                  type="checkbox"
                  id="all"
                  tabIndex={-1}
                  disabled={isSubmitting}
                  onClick={(e) => {
                    const { checked } = e.target as HTMLInputElement;

                    // 全選択／全解除
                    if (data?.data.illust_list_items == null) return;
                    const next: { [key: string]: boolean } = {};
                    data.data.illust_list_items.forEach((x) => {
                      next[x.illust_list_item_id?.toString() ?? ''] = checked;
                    });
                    reset(next);
                  }}
                />
                <label
                  htmlFor="all"
                  // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                  tabIndex={isSubmitting ? -1 : 0}
                  onKeyDown={(e) => {
                    if (e.code === 'Space') {
                      // TODO: document.getElementById を使わないようにする
                      document.getElementById('all')?.click();
                      e.preventDefault();
                    }
                  }}
                  style={{ marginBottom: '11px' }}
                >
                  &nbsp;
                </label>
              </span>
            </th>
            <th>
              {t('サムネイル')}
              <i className="fa-solid fa-sort" />
            </th>
            <SortableTh label={t('ファイル名')} onClick={() => changeSortKey('file_name')} />
            <SortableTh label={t('資料管理No')} onClick={() => changeSortKey('production_no')} />
            <SortableTh label={t('章')} onClick={() => changeSortKey('chapter_name')} />
            <SortableTh label={t('更新日時')} onClick={() => changeSortKey('updated_at')} />
            <th> </th>
          </tr>
        </thead>
        <tbody>
          {sortedIllustList.map(
            (x, i) =>
              N_DATA_PER_PAGE * (currentPage - 1) <= i &&
              i < N_DATA_PER_PAGE * currentPage && (
                <IllustTr
                  key={x.illust_list_item_id}
                  data={x}
                  visible={N_DATA_PER_PAGE * (currentPage - 1) <= i && i < N_DATA_PER_PAGE * currentPage}
                  register={register}
                />
              )
          )}
        </tbody>
      </table>
      {/* 隠れいているid */}
      {sortedIllustList.map(
        (x, i) =>
          !(N_DATA_PER_PAGE * (currentPage - 1) <= i && i < N_DATA_PER_PAGE * currentPage) && (
            <input
              key={`aaa_${x.illust_list_item_id?.toString()}`}
              type="hidden"
              name={x.illust_list_item_id?.toString() ?? ''}
            />
          )
      )}

      {/* ページング部分 */}
      <PageNavi
        current={currentPage}
        pageCount={Math.ceil((data?.data.illust_list_items?.length ?? 0) / (N_DATA_PER_PAGE * 1.0))}
        onChangePage={changeCurrentPage}
      />
      {/* 待ちモーダル */}
      {isSubmitting && <SpinnerModal />}
      {/* 複数ダウンロードエラー表示 */}
      {displayDownloadMultipleError && (
        <BlobErrorModal error={downloadMultipleError} onClickOk={() => setDisplayDownloadMultipleError(false)} />
      )}
      {/* 不要イラスト削除（機種選択）モーダル */}
      {displayProductionNoModal && productionNos !== undefined && (
        <SelectProductionNoModal
          productionNos={productionNos}
          onClose={() => setDisplayProductionNoModal(false)}
          refetch={refetch}
        />
      )}
      {/* ダウンロードエラー */}
      {displayMessageNotSelect && (
        <OkModal onClickOk={() => setDisplayMessageNotSelect(false)}>
          <div>{t('画像を選択してください。')}</div>
        </OkModal>
      )}
    </div>
  );
}

export default IllustListComponent;
